/**
 * Layer-2 outline/question proposal. Two modes, decided by whether the document
 * already has an outline:
 *
 * - **First run** (`existingSections` empty) — owns the whole empty area: idle
 *   empty state with the propose CTA (manual actions beside it) → generating →
 *   editable review → accept (writes the outline) | regenerate | discard.
 * - **Additions** (`existingSections` non-empty, narrative only) — sits ABOVE
 *   the outline table as a compact strip. The proposal is a set of NEW sections
 *   appended to what is already there; existing sections are never re-proposed,
 *   re-ordered, renumbered or replaced. Questionnaire re-extraction stays
 *   blocked backend-side, so this mode is outline-only.
 *
 * `manualActions` renders in every branch — including both review branches,
 * which previously dropped Add Section and Template for the whole review.
 *
 * Spec: response-structure-proposal-spec.md §3; firstrun-1-outline-additions.md §3.3.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Loader2, AlertCircle, RotateCcw, FileQuestion, ListTree, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { RegenerateProposalDialog } from '@/components/RegenerateProposalDialog'
import { cn } from '@/lib/utils'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { useOutlineProposalStore } from '@/stores/outline-proposal-store'
import { useEvaluationCriteriaStore } from '@/stores/evaluation-criteria-store'
import { useStrategyStore } from '@/stores/strategy-store'
import { findDuplicateTitleWarnings } from '@/lib/outline-duplicate-titles'
import { ProposedSectionRow } from './ProposedSectionRow'
import { QuestionProposalRows } from './QuestionProposalRows'
import type { OutlineProposal, QuestionsProposal, OutlineSection } from '@/lib/api'

/**
 * How many existing sections to show above the additions. Additions append, so
 * the tail is the insertion point — the full list already sits in the outline
 * table right below, and repeating all of it would be a second copy.
 */
const EXISTING_CONTEXT_ROWS = 3

/**
 * Shown on a hand-added row until it is named. Blocking the accept is not
 * optional: the backend's `title.min(1)` has no `.trim()`, so a whitespace-only
 * title persists as an unnamed section, and an empty one 400s the WHOLE payload
 * — losing the entire review rather than the one row (spec §2.4).
 */
const UNTITLED_SECTION_WARNING =
  'Give this section a title — an untitled section can’t be accepted.'

interface OutlineProposalAreaProps {
  bidId: string
  artifactId: string
  artifactType: 'narrative_document' | 'questionnaire'
  /**
   * Sections already in this document. Non-empty switches the area into
   * additions mode. Defaults to empty (first-run).
   */
  existingSections?: OutlineSection[]
  /** Existing manual-path actions (Add section / Template / Paste & Parse). */
  manualActions: ReactNode
  /** Called after a successful accept — parent refetches the outline. */
  onAccepted: () => void
  /**
   * Where the idle additions-mode "Propose more sections" trigger lives.
   * `inline` (default) renders it here, on a row of its own. `external` leaves
   * it to the page (`ProposeMoreSectionsButton`, e.g. in the progress row): the
   * idle branch then renders only its notes, or nothing, and every additions
   * branch carries its own bottom spacing so the page needs no wrapper.
   */
  additionsTrigger?: 'inline' | 'external'
}

export function OutlineProposalArea({
  bidId,
  artifactId,
  artifactType,
  existingSections = [],
  manualActions,
  onAccepted,
  additionsTrigger = 'inline',
}: OutlineProposalAreaProps) {
  const isQuestionnaire = artifactType === 'questionnaire'
  const isAdditions = !isQuestionnaire && existingSections.length > 0
  // External trigger: the page drops its wrapper, so the area spaces itself
  // off the row below — but only when it actually renders something.
  const isExternalTrigger = isAdditions && additionsTrigger === 'external'
  const additionsSpacing = isExternalTrigger ? 'mb-3' : undefined

  const {
    generation,
    sections,
    questions,
    isGenerating,
    isAccepting,
    error,
    isDirty,
    acceptWarnings,
    lastAddedSectionCount,
    start,
    load,
    addSection,
    updateSection,
    removeSection,
    moveSection,
    removeQuestion,
    accept,
    discard,
  } = useOutlineProposalStore()

  // Name maps for criteria/theme chips
  const criteria = useEvaluationCriteriaStore((s) => s.criteria)
  const fetchCriteria = useEvaluationCriteriaStore((s) => s.fetchCriteria)
  const themes = useStrategyStore((s) => s.themes)
  const loadThemes = useStrategyStore((s) => s.loadThemes)

  /** Row to focus after Add — a hand-added row arrives blank and unlabelled. */
  const [focusSectionKey, setFocusSectionKey] = useState<string | null>(null)

  useEffect(() => {
    // Resume an in-flight or completed (unaccepted) proposal on mount.
    void load(artifactId)
    if (!isQuestionnaire) {
      void fetchCriteria(bidId)
      void loadThemes(bidId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId])

  const criteriaNames = new Map(
    criteria.map((c) => [c.id, { title: c.title, weight: c.weighting?.percentage ?? null }])
  )
  const themeNames = new Map(themes.map((t) => [t.id, t.title]))

  const handleAccept = async () => {
    const ok = await accept(artifactId)
    if (ok) onAccepted()
  }

  const handleAddSection = () => {
    const key = addSection()
    if (key) setFocusSectionKey(key)
  }

  const proposeLabel = isQuestionnaire
    ? 'Extract questions'
    : isAdditions
      ? 'Propose more sections'
      : 'Propose outline'

  // ===================
  // Generating
  // ===================

  if (isGenerating) {
    if (isAdditions) {
      return (
        <InlineStrip className={additionsSpacing}>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[hsl(var(--ai-accent))]" />
          <span>
            Proposing more sections — reading the tender for what this outline doesn’t cover yet.
          </span>
        </InlineStrip>
      )
    }
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Loader2 className="mb-4 h-8 w-8 animate-spin text-[hsl(var(--ai-accent))]" />
        <h3 className="text-base font-medium mb-1">
          {isQuestionnaire ? 'Extracting questions…' : 'Proposing an outline…'}
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {isQuestionnaire
            ? 'Reading the schedule in the tender documents — questions are extracted word-for-word and verified against the source.'
            : 'Reading the tender for required formats, evaluation criteria and scope signals — every section will cite its reasoning. Usually under a minute.'}
        </p>
      </div>
    )
  }

  // ===================
  // Failed
  // ===================

  if (generation?.status === 'failed') {
    if (isAdditions) {
      return (
        <div className={additionsSpacing}>
          <ErrorNote message={error ?? 'Proposal failed'} />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ai-ghost" size="sm" onClick={() => void start(artifactId)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Try again
            </Button>
            {manualActions}
          </div>
        </div>
      )
    }
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="mb-4 h-8 w-8 text-destructive" />
        <h3 className="text-base font-medium mb-1">Proposal failed</h3>
        {error && <p className="text-sm text-muted-foreground max-w-sm">{error}</p>}
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={() => void start(artifactId)}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Try again
          </Button>
          {manualActions}
        </div>
      </div>
    )
  }

  // ===================
  // Review — questionnaire
  // ===================

  // Each review branch takes only its own kind of generation. The narrative
  // page can mount this area before its questionnaire redirect fires, and a
  // `questions` proposal read as an outline has no `sections` (2026-09-27).
  if (generation?.status === 'complete' && generation.kind === 'questions' && isQuestionnaire) {
    const proposal = generation.proposal as QuestionsProposal
    if (proposal.extraction.status === 'fallback') {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileQuestion className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-base font-medium mb-1">Questions not found</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            {proposal.notes[0] ??
              'Questions weren’t found in the uploaded documents — they may live in the procurement portal. Import them by pasting from the schedule.'}
          </p>
          <div className="flex items-center gap-2">{manualActions}</div>
        </div>
      )
    }
    return (
      <div className="w-full">
        <ReviewHeader
          title={`Extracted questions — ${questions.length} of ${proposal.questions.length} kept`}
          subtitle="Word-for-word from the schedule, verified against the source · text isn’t editable here (verbatim by contract) — edit after creating"
          acceptLabel={`Accept ${questions.length} question${questions.length === 1 ? '' : 's'}`}
          acceptDisabled={questions.length === 0 || isAccepting}
          isAccepting={isAccepting}
          isDirty={isDirty}
          onAccept={() => void handleAccept()}
          onRegenerate={() => void start(artifactId)}
          onDiscard={() => void discard(artifactId)}
        />
        {error && <ErrorNote message={error} />}
        <div className="rounded-xl border border-border bg-card shadow-sm">
          <QuestionProposalRows questions={questions} onRemove={removeQuestion} />
        </div>
        <ManualActionsRow>{manualActions}</ManualActionsRow>
      </div>
    )
  }

  // ===================
  // Review — narrative outline
  // ===================

  if (generation?.status === 'complete' && generation.kind === 'outline' && !isQuestionnaire) {
    const proposal = generation.proposal as OutlineProposal
    const totalWords = sections.reduce((sum, s) => sum + (s.suggestedWordCount ?? 0), 0)
    // Same-title collisions, computed against the document's real sections so
    // the reviewer sees them BEFORE accepting (spec §2-C3). The backend returns
    // the authoritative set on the accept response; this mirrors its
    // normalisation so the two agree.
    // A hand-added row is in `sections`, so it is subject to the same rule —
    // it neither bypasses the warning nor is rejected by it (spec §2.3).
    const duplicateWarnings = findDuplicateTitleWarnings(
      existingSections.map((s) => s.title),
      sections
    )
    // A blank row must not be acceptable as an unnamed section (spec §2.4).
    const hasUntitledSection = sections.some((s) => s.title.trim().length === 0)
    // Note lanes. Each producer emits ONE note per item, so a multi-document
    // bid with a big criteria tree can push ~30 notes into this area — every
    // group below the first collapses into a single banner rather than a wall
    // of uncapped full-width cards. Predicates mirror the backend note copy in
    // `outline-generation.ts` (appendCoverageNotes / appendUnanchoredSectionNotes).
    const coverageWarnings = proposal.notes.filter((n) => n.startsWith('Criterion'))
    const coveredElsewhere = proposal.notes.filter((n) => n.startsWith('Covered elsewhere:'))
    const unconfirmedCoverage = proposal.notes.filter((n) => n.startsWith('Coverage unconfirmed:'))
    const unanchoredSections = proposal.notes.filter((n) => n.startsWith('Section "'))
    const infoNotes = proposal.notes.filter(
      (n) =>
        !n.startsWith('Criterion') &&
        !n.startsWith('Covered elsewhere:') &&
        !n.startsWith('Coverage unconfirmed:') &&
        !n.startsWith('Section "')
    )

    const hiddenExistingCount = Math.max(0, existingSections.length - EXISTING_CONTEXT_ROWS)
    const contextSections = existingSections.slice(hiddenExistingCount)

    return (
      <div className={cn('w-full', additionsSpacing)}>
        <ReviewHeader
          title={
            isAdditions
              ? `Proposed additions — ${sections.length} new section${sections.length === 1 ? '' : 's'}${totalWords > 0 ? ` · ${totalWords.toLocaleString()} words` : ''}`
              : `Proposed outline — ${sections.length} section${sections.length === 1 ? '' : 's'}${totalWords > 0 ? ` · ${totalWords.toLocaleString()} words` : ''}`
          }
          subtitle={proposal.basisNote || basisLabel(proposal.structureBasis)}
          // Appending a not_started section lowers the bid's completion %
          // (`dashboard.ts:202-238` counts every section). It does NOT
          // un-finalise the document — finalised artifacts reject edits outright.
          footnote={
            isAdditions
              ? 'These are added to the end of the outline — nothing already in the document changes. Adding sections lowers this bid’s completion % until they’re drafted.'
              : undefined
          }
          acceptLabel={
            isAdditions
              ? `Add ${sections.length} section${sections.length === 1 ? '' : 's'}`
              : 'Accept outline'
          }
          acceptDisabled={sections.length === 0 || isAccepting || hasUntitledSection}
          isAccepting={isAccepting}
          isDirty={isDirty}
          onAccept={() => void handleAccept()}
          onRegenerate={() => void start(artifactId)}
          onDiscard={() => void discard(artifactId)}
        />

        {error && <ErrorNote message={error} />}

        {coverageWarnings.length > 0 && (
          <div className="mb-2 flex items-start gap-2 rounded-lg bg-[hsl(var(--status-warning)/0.12)] px-3 py-2 text-sm text-[hsl(var(--status-warning))]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {coverageWarnings.length === 1
                ? coverageWarnings[0]
                : `${coverageWarnings.length} criteria aren't mapped to any section: ${coverageWarnings
                    .map((n) => {
                      const m = n.match(/Criterion "([^"]+)"(?: \(([^)]+)\))?/)
                      return m ? `${m[1]}${m[2] ? ` (${m[2]})` : ''}` : n
                    })
                    .join(' · ')}`}
            </span>
          </div>
        )}
        {coveredElsewhere.length > 0 && (
          <InfoNote>
            {coveredElsewhere.length === 1
              ? coveredElsewhere[0]
              : `${coveredElsewhere.length} criteria are covered by other response documents: ${coveredElsewhere
                  .map(extractCoveredElsewhereLabel)
                  .join(' · ')}`}
          </InfoNote>
        )}
        {unconfirmedCoverage.length > 0 && (
          <InfoNote>
            {unconfirmedCoverage.length === 1
              ? unconfirmedCoverage[0]
              : `${unconfirmedCoverage.length} criteria couldn't be checked against the other response documents: ${unconfirmedCoverage
                  .map((n) => {
                    const m = n.match(/criterion "([^"]+)"(?: \(([^)]+)\))?/)
                    return m ? `${m[1]}${m[2] ? ` (${m[2]})` : ''}` : n
                  })
                  .join(' · ')} — map sections in those documents to confirm coverage.`}
          </InfoNote>
        )}
        {unanchoredSections.length > 0 && (
          <InfoNote>
            {unanchoredSections.length === 1
              ? unanchoredSections[0]
              : `${unanchoredSections.length} sections aren't tied to a criterion or tender citation: ${unanchoredSections
                  .map(extractUnanchoredSectionLabel)
                  .join(' · ')}`}
          </InfoNote>
        )}
        {infoNotes.map((note, i) => (
          <InfoNote key={`note-${i}`}>{note}</InfoNote>
        ))}

        <div className="mt-2 rounded-xl border border-border bg-card shadow-sm">
          {/* Additions mode: the tail of the real outline, read-only and at its
              real numbering, so "new" versus "already there" is unmistakable
              and nothing looks renumbered. */}
          {isAdditions && (
            <>
              {hiddenExistingCount > 0 && (
                <div className="px-4 py-2 text-meta">
                  {hiddenExistingCount} earlier section{hiddenExistingCount === 1 ? '' : 's'} in this
                  document
                </div>
              )}
              {contextSections.map((section, i) => (
                <div
                  key={section.sectionKey}
                  className="flex items-center gap-2 border-t border-border/60 px-4 py-2 text-sm text-muted-foreground first:border-t-0"
                >
                  <span className="w-5 shrink-0 text-xs">
                    {hiddenExistingCount + i + 1}
                  </span>
                  <span className="truncate">{section.title || 'Untitled section'}</span>
                </div>
              ))}
            </>
          )}
          {sections.map((section, index) => (
            <ProposedSectionRow
              key={section.key}
              section={section}
              displayNumber={existingSections.length + index + 1}
              isFirst={index === 0}
              isLast={index === sections.length - 1}
              isAddition={isAdditions}
              // An untitled row never collides (both normalise to ''), so the
              // two warnings can't contend for the slot.
              warning={
                section.title.trim().length === 0
                  ? UNTITLED_SECTION_WARNING
                  : (duplicateWarnings.get(section.key) ?? null)
              }
              autoFocusTitle={section.key === focusSectionKey}
              criteriaNames={criteriaNames}
              themeNames={themeNames}
              onUpdate={(patch) => updateSection(section.key, patch)}
              onRemove={() => removeSection(section.key)}
              onMove={(direction) => moveSection(section.key, direction)}
            />
          ))}
          {/* The reviewer spotted a section the model missed. It joins the
              review as an ordinary row — editable, dismissable, accepted with
              the rest — and is the only path that reports itself as
              `source: 'manual'`. The manual actions below the card write the
              outline DIRECTLY instead, skipping link and brief seeding. */}
          <div
            className={cn(
              'px-4 py-2',
              (sections.length > 0 || isAdditions) && 'border-t border-border/60'
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={handleAddSection}
              title="Add a blank section to this proposal"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add a section
            </Button>
          </div>
        </div>

        <ManualActionsRow>{manualActions}</ManualActionsRow>
      </div>
    )
  }

  // ===================
  // Idle — additions mode: a flat action strip above the outline table
  // ===================

  if (isAdditions) {
    const hasAcceptWarnings = acceptWarnings.length > 0
    const showAddedNote = lastAddedSectionCount > 0 && !hasAcceptWarnings
    // The trigger lives on the page — render the notes alone, and nothing at
    // all when there are none, so no empty band is left above the page's row.
    if (isExternalTrigger && !error && !hasAcceptWarnings && !showAddedNote) return null
    return (
      <div>
        {error && <ErrorNote message={error} />}
        {hasAcceptWarnings && (
          <div
            role="alert"
            className="mb-2 rounded-lg bg-[hsl(var(--status-warning)/0.12)] px-3 py-2 text-sm text-[hsl(var(--status-warning))]"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                {acceptWarnings.map((warning) => (
                  <p key={warning.sectionKey}>{warning.message}</p>
                ))}
              </div>
            </div>
          </div>
        )}
        {showAddedNote && (
          <InfoNote>
            {lastAddedSectionCount} section{lastAddedSectionCount === 1 ? '' : 's'} added — this
            bid’s completion % drops until they’re drafted.
          </InfoNote>
        )}
        {!isExternalTrigger && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ai-ghost" size="sm" onClick={() => void start(artifactId)}>
              <GradientSparkleIcon className="h-4 w-4 mr-2" />
              {proposeLabel}
            </Button>
            {manualActions}
          </div>
        )}
      </div>
    )
  }

  // ===================
  // Idle — first run: empty state with the propose CTA
  // ===================

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <ListTree className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
      <h3 className="text-base font-medium mb-1">
        {isQuestionnaire ? 'No questions yet' : 'No sections yet'}
      </h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-md">
        {isQuestionnaire
          ? 'Extract the questions word-for-word from the schedule in your tender documents — or add them manually.'
          : 'Propose an outline from the tender — required formats, evaluation criteria, scope of works — with a citation for every section. Or build it by hand.'}
      </p>
      {/* A start() that never produced a generation row (request rejected, or
          the poll timed out) leaves the failed branch unreachable — surface it
          here so the click visibly does something. */}
      {error && <ErrorNote message={error} className="max-w-md text-left" />}
      <div className="flex items-center gap-2">
        <Button onClick={() => void start(artifactId)}>
          <GradientSparkleIcon className="h-4 w-4 mr-2" />
          {proposeLabel}
        </Button>
        {manualActions}
      </div>
    </div>
  )
}

// ===================
// Internals
// ===================

/**
 * Manual paths stay reachable for the whole review — they used to disappear the
 * moment a proposal completed, stranding anyone who wanted to add a section by
 * hand instead (spec §3.3).
 */
function ManualActionsRow({ children }: { children: ReactNode }) {
  return <div className="mt-2 flex flex-wrap items-center gap-2">{children}</div>
}

/** Transient "a proposal is being generated" strip — the AI-notice tier. */
function InlineStrip({ children, className }: { children: ReactNode; className?: string | undefined }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-border border-l-2 border-l-[hsl(var(--ai-accent))] bg-muted px-3 py-2 text-sm text-foreground',
        className
      )}
    >
      {children}
    </div>
  )
}

/**
 * Coverage notices — unmapped criteria, covered elsewhere, unconfirmed coverage,
 * unanchored sections. Up to four render at once, so they stay neutral: they
 * report on the reviewer's own outline rather than on anything the model
 * produced, which is why they carry no AI marking and no sparkle.
 */
function InfoNote({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 flex items-start gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-foreground">
      {children}
    </div>
  )
}

/** `Covered elsewhere: criterion "X" (10%) is addressed by Y.` → `X (10%)`. */
function extractCoveredElsewhereLabel(note: string): string {
  const m = note.match(/criterion "([^"]+)"(?: \(([^)]+)\))?/)
  return m ? `${m[1]}${m[2] ? ` (${m[2]})` : ''}` : note
}

/** `Section "X" isn't tied to a criterion or a tender citation — …` → `X`. */
function extractUnanchoredSectionLabel(note: string): string {
  return note.match(/^Section "([^"]+)"/)?.[1] ?? note
}

function basisLabel(basis: OutlineProposal['structureBasis']): string {
  if (basis === 'prescribed') return 'Mirrors the structure the tender prescribes'
  if (basis === 'criteria') return 'Mirrors the evaluation criteria'
  return 'Derived from the tender’s scope of works'
}

function ReviewHeader(props: {
  title: string
  subtitle: string
  /** Consequence copy shown under the subtitle (additions: completion % drop). */
  footnote?: string | undefined
  acceptLabel: string
  acceptDisabled: boolean
  isAccepting: boolean
  /** Review edits are pending — regenerating would discard them. */
  isDirty: boolean
  onAccept: () => void
  onRegenerate: () => void
  onDiscard: () => void
}) {
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const { isDirty, onRegenerate } = props

  return (
    <div className="mb-3 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h3 className="text-sm font-medium">{props.title}</h3>
        <p className="mt-0.5 text-meta">{props.subtitle}</p>
        {props.footnote && (
          <p className="mt-0.5 text-meta">{props.footnote}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="ghost" size="sm" onClick={props.onDiscard}>
          Discard
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (isDirty ? setConfirmRegenerate(true) : onRegenerate())}
        >
          <RotateCcw className="h-4 w-4 mr-1.5" />
          Regenerate
        </Button>
        <Button size="sm" disabled={props.acceptDisabled} onClick={props.onAccept}>
          {props.isAccepting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {props.acceptLabel}
        </Button>
      </div>

      <RegenerateProposalDialog
        open={confirmRegenerate}
        onOpenChange={setConfirmRegenerate}
        onConfirm={() => {
          setConfirmRegenerate(false)
          onRegenerate()
        }}
      />
    </div>
  )
}

function ErrorNote({ message, className }: { message: string; className?: string }) {
  return (
    <div
      className={cn(
        'mb-3 flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive',
        className
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      {message}
    </div>
  )
}
